import React, { useState } from 'react';
import { registerScholarship, sendScholarshipOTP } from '../../services/api';
import Alert from '../common/Alert';
import '../common/RegistrationForm.css';

const ScholarshipRegistration = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    aadharNumber: '',
    email: '',
    phone: '',
    dob: '',
    scholarshipPercentage: '',
    adminEmail: '',
    otp: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [percentageError, setPercentageError] = useState('');
  const [alert, setAlert] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate scholarship percentage
    const perc = parseFloat(formData.scholarshipPercentage);
    if (isNaN(perc) || perc < 0 || perc > 100) {
      setAlert({ type: 'error', message: 'max you can give is 100 only' });
      setIsSubmitting(false);
      return;
    }

    if (!otpSent) {
      setAlert({ type: 'error', message: 'Please verify admin OTP first.' });
      setIsSubmitting(false);
      return;
    }

    if (!formData.otp) {
      setAlert({ type: 'error', message: 'Please enter the OTP.' });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await registerScholarship(formData);
      setAlert({
        type: 'success',
        message: `Registration successful! Your scholarship code is ${response.data.scholarshipCode}`
      });
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        aadharNumber: '',
        email: '',
        phone: '',
        dob: '',
        scholarshipPercentage: '',
        adminEmail: '',
        otp: ''
      });
      setOtpSent(false);
    } catch (error) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'Registration failed'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    if (!formData.adminEmail) {
      setAlert({ type: 'error', message: 'Please select an admin email for OTP verification.' });
      return;
    }
    
    setIsSubmitting(true);
    setAlert(null);
    try {
      await sendScholarshipOTP({ email: formData.adminEmail });
      setOtpSent(true);
      setAlert({ type: 'success', message: 'OTP sent successfully to admin email!' });
    } catch (error) {
      setAlert({ 
        type: 'error', 
        message: error.response?.data?.message || 'Failed to send OTP' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-container">
      <div className="registration-form-wrapper">
        <img 
          src="/assets/BigLogo.png" 
          alt="Educatory Logo" 
          className="educatory-logo"
        />
        <h2>Scholarship Registration</h2>
        <p className="subtitle">Register to receive your scholarship code</p>

        {alert && (
          <Alert 
            message={alert.message} 
            type={alert.type} 
            onClose={() => setAlert(null)} 
          />
        )}

        <form onSubmit={otpSent ? handleSubmit : handleSendOTP} className="registration-form">
          <div className="form-row three-cols">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value.toUpperCase()})}
                style={{ textTransform: 'uppercase' }}
                required
                placeholder="Enter first name"
              />
            </div>
            <div className="form-group">
              <label>Middle Name</label>
              <input
                type="text"
                value={formData.middleName}
                onChange={(e) => setFormData({...formData, middleName: e.target.value.toUpperCase()})}
                style={{ textTransform: 'uppercase' }}
                placeholder="Enter middle name"
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value.toUpperCase()})}
                style={{ textTransform: 'uppercase' }}
                required
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="Enter your email address"
              />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
                pattern="[0-9]{10}"
                placeholder="Enter 10-digit phone number"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Aadhar Number *</label>
              <input
                type="text"
                value={formData.aadharNumber}
                onChange={(e) => setFormData({...formData, aadharNumber: e.target.value})}
                required
                pattern="[0-9]{12}"
                placeholder="Enter 12-digit Aadhar number"
              />
            </div>
            <div className="form-group">
              <label>Date of Birth *</label>
              <input
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData({...formData, dob: e.target.value})}
                required
                disabled={otpSent}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Scholarship Percentage (%) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.scholarshipPercentage}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({...formData, scholarshipPercentage: val});
                  if (parseFloat(val) > 100) {
                    setPercentageError('max you can give is 100 only');
                  } else {
                    setPercentageError('');
                  }
                }}
                required
                disabled={otpSent}
                placeholder="Enter % (Max 100)"
                style={percentageError ? { borderColor: '#dc2626', outline: 'none' } : {}}
              />
              {percentageError && (
                <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>
                  {percentageError}
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Select Email for OTP Verification</label>
              <select
                value={formData.adminEmail}
                onChange={(e) => setFormData({...formData, adminEmail: e.target.value})}
                required
                disabled={otpSent}
              >
                <option value="">Select Admin Email</option>
                <option value="alok.iitdelhi@educatory.ac">alok.iitdelhi@educatory.ac</option>
                <option value="cfo@educatory.ac">cfo@educatory.ac</option>
              </select>
            </div>
          </div>

          {otpSent && (
            <div className="form-row">
              <div className="form-group otp-group">
                <label>Enter 6-Digit OTP</label>
                <input
                  type="text"
                  value={formData.otp}
                  onChange={(e) => setFormData({...formData, otp: e.target.value})}
                  required
                  placeholder="Enter 6-digit OTP"
                  maxLength="6"
                  pattern="[0-9]{6}"
                />
              </div>
            </div>
          )}

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting || (!otpSent && (percentageError || parseFloat(formData.scholarshipPercentage) > 100 || !formData.adminEmail))}
          >
            {isSubmitting ? 'Processing...' : (otpSent ? 'Verify OTP & Register' : 'Send OTP')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ScholarshipRegistration;
